import React from "react";

const article = {
  slug: "eicr-checklist-for-industrial-premises",
  title: "EICR checklist for industrial premises: what to prepare, what to expect",
  dek: "A practical, UK-focused guide to preparing your factory or commercial site for an Electrical Installation Condition Report — what electricians look at, what the observation codes mean, and how to keep the disruption minimal.",
  category: "Compliance",
  readTime: "8 min read",
  publishedAt: "2026-02-05",
  image: "/gallery/db-panel-3.jpg",
  imagePosition: "center 40%",
  relatedServiceSlugs: [
    "eicr-and-fixed-wire-testing",
    "thermal-imaging-and-condition-monitoring",
    "planned-preventive-maintenance",
    "industrial-electrical-installation",
  ],
  faqs: [
    {
      q: "How often should an industrial site have an EICR?",
      a: "There is no single mandated interval — inspection frequency is risk-based under the Electricity at Work Regulations 1989 and BS 7671. The IET's Guidance Note 3 gives recommended maximum periods depending on the type of installation and its usage; industrial premises are typically inspected more frequently than low-risk commercial ones. Your inspector will recommend a period at the end of the report.",
    },
    {
      q: "What do the observation codes on an EICR mean?",
      a: "C1 indicates danger present and immediate remedial action is required. C2 indicates potentially dangerous and urgent remedial action is required. C3 is an improvement recommendation — the installation is safe but could be brought closer to current standards. FI means Further Investigation is required. Any C1 or C2 result classifies the overall installation as unsatisfactory until fixed.",
    },
    {
      q: "Do we have to shut the site down for testing?",
      a: "Not always. Testing includes energised (live) checks that only take moments and dead tests that need circuits isolated. A competent contractor will plan the visit around your production schedule — often testing one distribution board or area at a time to keep the site running. Talk to your electrician early about acceptable downtime windows.",
    },
    {
      q: "Who is legally responsible for the EICR?",
      a: "The duty holder — usually the employer or the person in control of the premises — is responsible under the Electricity at Work Regulations 1989 for ensuring the electrical installation is maintained in a safe condition. An EICR is one of the main ways duty holders evidence that they have taken this obligation seriously.",
    },
  ],
  body: (
    <>
      <p>
        An Electrical Installation Condition Report — an EICR — is the periodic
        inspection every fixed electrical installation in the UK is expected to
        have. On an industrial premises, it&apos;s not paperwork for its own
        sake: it&apos;s the document that tells you whether the electrical
        infrastructure you rely on every day is still safe, still compliant
        with BS 7671, and still likely to keep your production line, freezers,
        motor control centres and life-safety systems working.
      </p>
      <p>
        The problem is that EICRs are usually scheduled to a date, then quietly
        forgotten about until the electrician turns up. That&apos;s when
        avoidable disruption happens — panels can&apos;t be opened, drawings
        can&apos;t be found, and half the surveys have to be booked again. This
        guide walks through what a good industrial EICR actually covers and how
        to prepare so the inspector can get through the work efficiently.
      </p>

      <h2>What an EICR really covers</h2>
      <p>
        Under BS 7671 (the IET Wiring Regulations, currently in the 18th
        Edition with amendments), the fixed electrical installation is defined
        as everything from the point where the DNO&apos;s supply enters the
        premises through to the final circuits — distribution boards, sub-mains,
        wiring, accessories, motor control centres and connected equipment
        forming part of the installation. An EICR is a documented condition
        report on that fixed installation.
      </p>
      <p>Typically it comprises:</p>
      <ul>
        <li>Visual inspection of accessible switchgear, panels, containment and terminations</li>
        <li>Dead testing — continuity of protective conductors, insulation resistance, polarity, earth fault loop impedance where accessible</li>
        <li>Live testing — earth fault loop impedance (Ze / Zs), prospective fault current, RCD/RCBO trip characteristics</li>
        <li>Verification of protective device selection, ratings and discrimination</li>
        <li>A written schedule of circuits and observations against a defined coding system</li>
      </ul>
      <p>
        On industrial premises the inspector will also want to see any special
        installations — three-phase distribution, hazardous area equipment,
        UPS backfeeds, generator connections, external supplies to plant, EV
        chargers if fitted and standby lighting.
      </p>

      <h2>Preparing the site — a checklist</h2>
      <p>
        The single biggest cause of an EICR overrunning is access. Every hour
        the inspector spends looking for a key, chasing an isolator or waiting
        for someone to sign a permit is an hour of downtime you pay for. The
        checklist below is the one we hand to clients on booking.
      </p>
      <ul>
        <li>
          <strong>Drawings.</strong> Locate the most recent single-line diagram
          and distribution schedule. If none exist, expect the inspector to
          spend time mapping the installation manually.
        </li>
        <li>
          <strong>Access.</strong> Confirm keys, permits and escorts for
          switch rooms, plant rooms, roof plant, sub-station enclosures and any
          hazardous or restricted areas.
        </li>
        <li>
          <strong>Sampling agreement.</strong> Under BS 7671 an EICR can be
          based on sampling, but the sample size and rationale must be agreed
          and documented. Agree this in writing before the visit.
        </li>
        <li>
          <strong>Isolations.</strong> List anything that must NOT be dead-tested
          during the visit — process controllers, batching systems, freezers,
          uninterruptible loads. Agree how these will be handled or deferred.
        </li>
        <li>
          <strong>Previous reports.</strong> Provide the last EICR and any
          Minor Works or Electrical Installation Certificates issued since,
          plus records of remedial work.
        </li>
        <li>
          <strong>PAT scope.</strong> Clarify that portable appliance testing
          is separate from an EICR. If you want it done at the same visit, book
          it in advance.
        </li>
      </ul>

      <h2>Understanding the observation codes</h2>
      <p>
        Every observation the inspector records is coded so the overall result
        — <em>satisfactory</em> or <em>unsatisfactory</em> — is unambiguous:
      </p>
      <ul>
        <li>
          <strong>C1</strong> — Danger present. Immediate remedial action
          required. Anything from exposed live parts to broken bonding on a
          large process installation will land here. Any C1 makes the report
          unsatisfactory.
        </li>
        <li>
          <strong>C2</strong> — Potentially dangerous. Urgent remedial action
          required. A missing earth on a downstream circuit, an RCD that
          won&apos;t trip within its stated time, damaged insulation — issues
          that could reasonably become a hazard. C2 also fails the report.
        </li>
        <li>
          <strong>C3</strong> — Improvement recommended. The installation is
          still safe but not to the current edition of BS 7671; for example,
          older colour coding or lack of RCD protection where a new install
          would require it.
        </li>
        <li>
          <strong>FI</strong> — Further Investigation required. The inspector
          couldn&apos;t determine whether the item is safe or not — commonly
          because of restricted access, unusual configuration or a suspected
          latent fault. FI also fails the report.
        </li>
      </ul>

      <h2>After the report — what to do with it</h2>
      <p>
        The report is not the end of the process. A duty holder is expected to
        act on the findings within a reasonable period — measured in days for
        C1 items and weeks or a few months for C2 items — and to record what
        was done. Treat the EICR as the start of a remedials plan:
      </p>
      <ol>
        <li>Triage the observations by code and by production impact.</li>
        <li>Ask the contractor to quote remedials with recommended windows.</li>
        <li>Schedule works during planned downtime where possible.</li>
        <li>
          Retain the follow-up Minor Works Certificates or Electrical
          Installation Certificates issued for each remedial — these evidence
          that the original observations have been closed out.
        </li>
        <li>
          Add the next inspection due date to your maintenance calendar and
          include reactive interim checks under your PPM programme.
        </li>
      </ol>

      <h2>Keeping EICRs painless year on year</h2>
      <p>
        The premises that get through EICRs most easily are the ones where the
        electrical infrastructure is maintained the rest of the time.
        Consistent labelling of distribution boards and circuits, up-to-date
        schedules, sensible IP-rated enclosures in wet areas, and a
        thermographic survey between EICRs to catch loose terminations early
        — all of these turn the periodic inspection from an anxious event into
        a formality.
      </p>
      <p>
        A short list of habits that materially reduce the number of C1, C2
        and FI observations from one inspection to the next:
      </p>
      <ul>
        <li>
          Update the single-line diagram every time a distribution board is
          modified — not once a year in a rush. A drawing that reflects the
          installation as it actually stands makes every future inspection,
          repair and modification quicker and safer.
        </li>
        <li>
          Standardise on a single wire-numbering and label convention across
          the site. When ten different electricians have worked on ten
          different panels over five years, the inspector has to translate at
          every stop; standardisation removes half of that time.
        </li>
        <li>
          Isolate and label anything that is out of service. An unused motor
          circuit still connected to the busbar is a fault waiting to happen
          and an observation waiting to be written.
        </li>
        <li>
          Keep RCD test buttons accessible and use them monthly — recording
          the result. RCDs that have never been tested manually and then
          fail their trip characteristic at inspection are one of the most
          common C2 findings.
        </li>
      </ul>
      <p>
        If it's been three years or more since your last EICR, or if you
        cannot lay your hands on the report, get one booked. Your insurer,
        your auditor and — most importantly — the people who work on your
        equipment will thank you.
      </p>
    </>
  ),
};

export default article;
